import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartTooltip } from "@/components/ChartTooltip";
import { ChartEmptyState } from "@/components/states";
import { formatChartLabel } from "@/lib/format";

const LINE_CHARS = 20;

/**
 * Split a category name into up to two lines, choosing the word/hyphen
 * boundary that keeps the longest line shortest. Only when no split fits
 * does an ellipsis appear — the full name always rides along as a hover
 * <title>.
 */
function wrapLabel(name: string): string[] {
  if (name.length <= LINE_CHARS) return [name];

  let best = -1;
  let bestMax = Infinity;
  for (let i = 1; i < name.length - 1; i++) {
    if (name[i] === " " || name[i] === "-") {
      const firstLen = name[i] === "-" ? i + 1 : i;
      const secondLen = name.length - (i + 1);
      const max = Math.max(firstLen, secondLen);
      if (max < bestMax) {
        bestMax = max;
        best = i;
      }
    }
  }
  if (best === -1) {
    return [`${name.slice(0, LINE_CHARS - 1)}…`];
  }

  const clip = (s: string) =>
    s.length > LINE_CHARS ? `${s.slice(0, LINE_CHARS - 1)}…` : s;
  const first = name.slice(0, name[best] === "-" ? best + 1 : best);
  const second = name.slice(best + 1);
  return [clip(first.trimEnd()), clip(second.trimStart())];
}

function CategoryTick({
  x,
  y,
  payload,
}: {
  x?: number;
  y?: number;
  payload?: { value?: string | number };
}) {
  const name = String(payload?.value ?? "");
  const lines = wrapLabel(name);
  return (
    <g>
      <title>{name}</title>
      <text
        x={x}
        y={y}
        textAnchor="end"
        fill="var(--viz-ink-secondary)"
        fontSize={12}
      >
        {lines.map((line, i) => (
          <tspan key={i} x={x} dy={i === 0 ? (lines.length > 1 ? -3 : 4) : 14}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
}

/**
 * Ranked horizontal bars: one series, values labeled at the tips,
 * long category names wrapped to two lines (full name in the tooltip).
 */
export function HorizontalBarChart({
  data,
  seriesName,
  categoryWidth = 150,
}: {
  data: { name: string; value: number }[];
  seriesName: string;
  categoryWidth?: number;
}) {
  if (data.length === 0) {
    return <ChartEmptyState />;
  }
  return (
    <ResponsiveContainer width="100%" height={Math.max(160, data.length * 40)}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 0, right: 48, bottom: 0, left: 8 }}
      >
        <CartesianGrid stroke="var(--viz-grid)" horizontal={false} />
        <XAxis type="number" hide />
        <YAxis
          type="category"
          dataKey="name"
          tick={<CategoryTick />}
          axisLine={{ stroke: "var(--viz-baseline)" }}
          tickLine={false}
          width={categoryWidth}
        />
        <Tooltip
          content={<ChartTooltip />}
          cursor={{ fill: "var(--viz-grid)", opacity: 0.4 }}
        />
        <Bar
          name={seriesName}
          dataKey="value"
          fill="var(--viz-series-1)"
          barSize={20}
          radius={[0, 4, 4, 0]}
          isAnimationActive={false}
        >
          <LabelList
            dataKey="value"
            position="right"
            formatter={(v: number) => formatChartLabel(v)}
            style={{ fill: "var(--viz-ink-secondary)", fontSize: 12 }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
